import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { authService, User as AuthUser } from './auth';
import { apiUtils, getErrorMessage } from './api';

type User = AuthUser;

interface LoginResult extends User {
  needsPhoneVerification?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Start with null — the useEffect verify call will populate this from the server
  // (prevents stale is_teacher / is_admin values from localStorage)
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [token, setToken] = useState<string | null>(() => apiUtils.getAuthToken());


  useEffect(() => {
    const initAuth = async () => {
      try {
        // If we already have a token stored, attempt to get current user from authService
        // Only verify if we have a stored token
        if (apiUtils.isAuthenticated()) {
          const current = await authService.getCurrentUser?.();
          if (current) {
            setUser(current as User);
            setIsAuthenticated(true);
          } else {
            // Token invalid / expired — clear everything
            apiUtils.removeAuthToken();
            setUser(null);
            setIsAuthenticated(false);
          }
        }
      } catch (err) {
        console.error('Failed to initialize auth:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);


  /**
   * login
   * - Normalizes email
   * - Calls authService.login
   * - Throws on failure so callers (LoginPage) can handle and avoid navigation
   * - Persists token and user via apiUtils on success
   */
  const login = async (email: string, password: string): Promise<LoginResult> => {
    setError(null);
    setLoading(true);

    try {
      const normalizedEmail = String(email).trim().toLowerCase();

      // Call auth service (this may throw on network error)
      const res: any = await authService.login({ email: normalizedEmail, password });

      // Handle different possible shapes of response from authService
      // Expected successful shape: { success: true, token, user } OR { token, user }
      if (!res) {
        const msg = 'Authentication failed (no response)';
        setError(msg);
        throw new Error(msg);
      }

      // If the service returned a success flag that's false -> treat as failure
      if (res.success === false) {
        const msg = res.message || 'Authentication failed';
        setError(msg);
        throw new Error(msg);
      }

      // Ensure token and user exist
      const token = res.token || res.data?.token;
      const userPayload = res.user || res.data?.user || res;
      const needsPhoneVerification = res.needsPhoneVerification || false;

      if (!token || !userPayload) {
        const msg = res.message || 'Authentication failed: invalid response from server';
        setError(msg);
        throw new Error(msg);
      }

      // Persist auth data
      try {
        apiUtils.setAuthToken(token);
        apiUtils.setUserData(userPayload);
      } catch (e) {
        console.warn('Could not persist auth data to localStorage:', e);
      }

      setUser(userPayload as User);
      setIsAuthenticated(true);
      setToken(token);
      setLoading(false);

      // Return user with needsPhoneVerification flag
      return { ...userPayload, needsPhoneVerification } as LoginResult;
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      setLoading(false);
      // Re-throw so callers (LoginPage) will catch and avoid navigating
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      // ✅ NEW: Call backend logout endpoint
      await authService.logout();
      
      apiUtils.removeAuthToken();
    } catch (e) {
      console.warn('Error during logout:', e);
    }
    setUser(null);
    setIsAuthenticated(false);
    setToken(null);
    setError(null);
  };

  /**
   * updateUser
   * - Partially updates the current user state
   * - Persists the updated user to apiUtils
   */
  const updateUser = (userData: Partial<User>) => {
    if (!user) return;
    const updatedUser = { ...user, ...userData };
    
    try {
      apiUtils.setUserData(updatedUser);
    } catch (e) {
      console.warn('Could not persist updated user to localStorage:', e);
    }
    
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, updateUser, isAuthenticated, token }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};